import {Checkbox, Form, Input, notification, Space, Typography} from "@ui";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import GoogleSignIn from "@/components/Auth/GoogleSignIn";
import {PrimaryButton} from "@/components/Button";
import useIsMobile from "@/hooks/useIsMobile";
import useUserRegistration from "@/hooks/useUserRegistration";
import {getCookie} from "@/providers/cookieStorage";
import {getMetaPixelParams} from "@/util/urlParams";
import eyeActive from "../../../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../../../assets/images/icon-eye-inactive.svg";
import defaultRegistrationDesktop from "../../../../assets/images/RegistrationImage.avif";
import defaultRegistrationMobile from "../../../../assets/images/RegistrationMobile.avif";
import {useAppConfig} from "../../../../store/useAppConfig.store";
import {useAuthStore} from "../../../../store/useAuth.store";
import {useLogo} from "../../../../util/logoUtils";
import AccountValidation from "../AccountValidation";
import styles from "../RegistrationModal/registrationModal.module.css";
import {EmailInput} from "./components";

const RegistrationModal = ({modalOpen = true}) => {
  const [form] = Form.useForm();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [registerButtonClicked, setRegisterButtonClicked] = useState(false);

  const logo = useLogo();

  const {mutate: postUserRegistration, isPending, error} = useUserRegistration();
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const {t} = useTranslation();
  const isMobile = useIsMobile({breakpoint: 920});

  const authModalImages = useAppConfig(state => state.authModalImages);
  const registrationDesktopImage = authModalImages?.registrationDesktopImage || defaultRegistrationDesktop;
  const registrationMobileImage = authModalImages?.registrationMobileImage || defaultRegistrationMobile;

  useEffect(() => {
    if (error) {
      const errorMessage = error?.message || error?.toString();

      if (errorMessage === "user_already_exists") {
        form.setFields([
          {
            name: "email",
            errors: [t("registrationEmailAlreadyExists", "Este email ya está registrado")],
          },
        ]);
      } else {
        notification.error({
          message: "Error en el registro",
          description: errorMessage || "Ha ocurrido un error inesperado",
        });
      }
    }
  }, [error, form, t]);

  const onFinish = values => {
    setRegisterButtonClicked(true);

    const referred = getCookie("referred");
    const {fbp, fbc} = getMetaPixelParams();

    const sendData = {
      email: values.email?.trim().toLowerCase() ?? values.email,
      password: values.password,
      referral_code: referred ?? "",
      preferred_bonus: "slot",
    };

    if (fbp) {
      sendData.fbp = fbp;
    }
    if (fbc) {
      sendData.fbc = fbc;
    }

    postUserRegistration(sendData);
  };

  const isValidEmail = email => {
    if (!email) {
      return {valid: false, message: t("registrationEmail_required")};
    }
    const specialCharRegex = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ªº·#$%^&*+=\\|~<>¿?]/;
    if (specialCharRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_specialChar")};
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
    if (!emailRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_message")};
    }
    const [localPart, domainPart] = email.split("@");
    if (
      localPart.startsWith(".") ||
      localPart.endsWith(".") ||
      localPart.startsWith("-") ||
      localPart.startsWith("+") ||
      localPart.endsWith("-") ||
      localPart.endsWith("+")
    ) {
      return {valid: false, message: t("registrationEmail_CharStartEnd")};
    }
    if (
      localPart.includes("..") ||
      domainPart.includes("..") ||
      domainPart.includes("--") ||
      localPart.includes("--")
    ) {
      return {valid: false, message: t("registrationEmail_CharConsecutive")};
    }
    const domainParts = domainPart.split(".");
    for (const part of domainParts) {
      if (part.startsWith("-") || part.endsWith("-")) {
        return {valid: false, message: t("registrationEmail_CharStartEnd")};
      }
    }
    return {valid: true, message: ""};
  };

  return (
    <div
      style={{
        flexDirection: !isMobile ? "row" : "column",
      }}
      className={styles.registration_modal}
    >
      {!isMobile ? (
        <div
          style={{
            position: "relative",
            minWidth: 450,
            maxWidth: 700,
            borderRadius: "6px 0 0 6px",
            overflow: "hidden",
          }}
        >
          <img
            src={registrationDesktopImage}
            alt="logo"
            className={styles.registration_modal_image}
          />
        </div>
      ) : (
        <div style={{width: "100%", display: "flex"}}>
          <img
            src={registrationMobileImage}
            alt="banner"
            style={{width: "100%", borderRadius: "18px 18px 0 0"}}
          />
        </div>
      )}
      <Form
        form={form}
        name="registrationModal"
        onFinish={onFinish}
        layout="vertical"
        className={styles.form_container_login}
        autoComplete="off"
        style={{
          flex: 1,
          background: "var(--modal-bg)",
          display: "flex",
          flexDirection: "column",
          minWidth: isMobile ? "100%" : 350,
          maxWidth: 671,
          borderRadius: isMobile ? "0 0 20px 20px" : "0 6px 6px 0",
        }}
      >
        <Form.Item className={styles.form_container_login_form}>
          {!isMobile && (
            <img
              src={logo}
              alt="logo"
              className={styles.logo_login}
            />
          )}
          <h2 className={styles.centerTitle}>{t("baseModalTitleRegistration")}</h2>
          <Form.Item style={{textAlign: "center", width: "100%"}}>
            <GoogleSignIn
              shape="rectangular"
              active={modalOpen}
              mode="register"
            />
          </Form.Item>
          <Form.Item
            name="email"
            normalize={v => (typeof v === "string" ? v.trim() : v)}
            rules={[
              {required: true, message: t("registrationEmail_required")},
              {
                validator: (_, value) => {
                  if (value) {
                    const trimmedValue = value.trim();
                    const result = isValidEmail(trimmedValue);
                    if (!result.valid) {
                      return Promise.reject(new Error(result.message));
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
            style={{width: "100%"}}
          >
            <EmailInput placeholder={t("loginEmailLocalPartPlaceholder")} />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{required: true}, {min: 8, message: t("registrationPasswordMinLength")}]}
            style={{width: "100%"}}
          >
            <Input.Password
              className={styles.Input}
              placeholder={t("registrationPassword")}
              iconRender={visible => (
                <img
                  src={visible ? eyeActive : eyeInactive}
                  alt="toggle password"
                  style={{width: 24, height: 24}}
                />
              )}
            />
          </Form.Item>
          <Form.Item
            name="repeat_password"
            dependencies={["password"]}
            rules={[
              {required: true, message: t("registrationRepeatPassword_required")},
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("registrationPasswordNotMatch")));
                },
              }),
            ]}
            style={{width: "100%"}}
          >
            <Input.Password
              className={styles.Input}
              placeholder={t("registrationRepeatPassword")}
              iconRender={visible => (
                <img
                  src={visible ? eyeActive : eyeInactive}
                  alt="toggle password"
                  style={{width: 24, height: 24}}
                />
              )}
            />
          </Form.Item>
          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[{required: true, message: t("registrationAcceptTerms")}]}
            style={{width: "100%"}}
            className={styles.customCheckbox}
          >
            <Checkbox onChange={e => setAgreeToTerms(e.target.checked)}>
              <p
                style={{color: "var(--text-primary)"}}
                className={styles.checkboxText}
              >
                Acepto los{" "}
                <a
                  href="/terminos-y-condiciones"
                  target="_blank"
                  rel="noreferrer"
                  style={{color: "var(--action-primary)"}}
                >
                  términos y condiciones
                </a>
                . Y ser mayor de 18 años de edad.
              </p>
            </Checkbox>
          </Form.Item>
          <Form.Item style={{textAlign: "center", width: "100%"}}>
            <PrimaryButton
              htmlType="submit"
              disabled={!agreeToTerms}
              loading={isPending}
              style={isMobile ? {width: "100%"} : undefined}
            >
              {t("registrationAcceptTermsRegister")}
            </PrimaryButton>
          </Form.Item>
          <Form.Item style={{textAlign: "center", width: "100%"}}>
            <Space size="middle">
              <Typography.Text>¿Ya tienes una cuenta?</Typography.Text>
              <Typography.Text
                onClick={() => handleAuthForm("login")}
                className={styles.hover_login}
                style={{color: "var(--action-primary)", cursor: "pointer"}}
              >
                Iniciar sesión
              </Typography.Text>
            </Space>
          </Form.Item>
          {registerButtonClicked && error && (
            <AccountValidation
              error={error}
              onClose={() => setRegisterButtonClicked(false)}
            />
          )}
        </Form.Item>
      </Form>
    </div>
  );
};
export default RegistrationModal;
